// React imports
import React, { useState } from "react";

// External library imports
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * A single node in the simple, read-only-for-structure hierarchical KPI tree
 * (spec S-10/S-11): CEO KPI -> Department/Role KPIs -> Team KPIs -> Individual KPIs.
 */
export interface KPITreeNode {
  kpi_id: string;
  kpi_code?: string | null;
  title: string;
  threshold?: number | null;
  weight?: number; // weight of the linkage connecting this node to its parent
  linkage_id?: string;
  children: KPITreeNode[];
}

interface KPITreeViewProps {
  nodes: KPITreeNode[];
  onSelectNode?: (node: KPITreeNode) => void;
  selectedKpiId?: string | null;
}

const KPITreeNodeItem: React.FC<{
  node: KPITreeNode;
  depth: number;
  onSelectNode?: (node: KPITreeNode) => void;
  selectedKpiId?: string | null;
}> = ({ node, depth, onSelectNode, selectedKpiId }) => {
  const [open, setOpen] = useState<boolean>(depth < 1);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <ListItemButton
        selected={selectedKpiId === node.kpi_id}
        onClick={() => onSelectNode?.(node)}
        sx={{ pl: 2 + depth * 2, borderRadius: 1 }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
          sx={{ mr: 0.5, visibility: hasChildren ? "visible" : "hidden" }}
        >
          {hasChildren ? (
            open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {node.kpi_code || node.kpi_id.slice(0, 8)}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
                {node.title}
              </Typography>
              {node.weight !== undefined && (
                <Chip size="small" label={`w=${node.weight}`} variant="outlined" />
              )}
              {node.threshold !== undefined && node.threshold !== null && (
                <Chip size="small" label={`thr=${node.threshold}`} variant="outlined" color="primary" />
              )}
            </Box>
          }
        />
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            {node.children.map((child) => (
              <KPITreeNodeItem
                key={`${child.linkage_id || child.kpi_id}`}
                node={child}
                depth={depth + 1}
                onSelectNode={onSelectNode}
                selectedKpiId={selectedKpiId}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * Simple collapsible KPI hierarchy tree. Read-only for structure - editing
 * weights happens exclusively in the linkage form (spec S-12).
 */
const KPITreeView: React.FC<KPITreeViewProps> = ({ nodes, onSelectNode, selectedKpiId }) => {
  if (!nodes || nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No linkages yet. Create a linkage below to start building the hierarchy.
      </Typography>
    );
  }

  return (
    <List dense>
      {nodes.map((node) => (
        <KPITreeNodeItem
          key={node.kpi_id}
          node={node}
          depth={0}
          onSelectNode={onSelectNode}
          selectedKpiId={selectedKpiId}
        />
      ))}
    </List>
  );
};

export default KPITreeView;
